import { useParams, useNavigate } from "react-router-dom";
import { BLOGS_DATA } from "./const";
import { Fragment } from "react/jsx-runtime";
import React from "react";
import { Link } from "react-router-dom";
import { Meta } from "@/components";

const formatTrademark = (text: string) => {
  if (typeof text !== "string") return text;
  
  // Create a regex to split by both ® and ™
  const parts = text.split(/(®|™)/g);
  
  return parts.map((part, index) => {
    if (part === "®" || part === "™") {
      return <sup key={index}>{part}</sup>;
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};

const SpecificBlog = () => {
  const { id } = useParams();

  const blog = BLOGS_DATA.find((b) => b.blogId === id);

  if (!blog) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="text-2xl font-bold">Blog not found</h1>
      </div>
    );
  }

  return (
    <>
      <Meta title={`Intron - ${blog.title}`} />
      <div className="bg-[#1F3342] text-white py-20 px-4 text-center">
        <nav aria-label="Breadcrumb" className="mb-4">
          <ol
            className="
        flex flex-wrap justify-center items-center 
        gap-2 
        text-base sm:text-lg 
        font-semibold
      "
          >
            <li>
              <Link to="/" className="text-primary hover:underline">
                Home
              </Link>
            </li>

            <li className="text-white">/</li>

            <li>
              <Link to="/blogs" className="text-primary hover:underline">
                Blogs
              </Link>
            </li>

            <li className="text-white">/</li>

            <li className="text-white font-bold max-w-[250px] sm:max-w-none text-center truncate sm:whitespace-normal">
               {formatTrademark(blog.title)}
            </li>
          </ol>
        </nav>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Heading */}
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
           {formatTrademark(blog.title)}
        </h1>
        

        {/* Subtitle if available */}
        {blog.subTitle && (
          <h2 className="text-xl font-semibold text-gray-700 mb-6">
              {formatTrademark(blog.subTitle)}
          </h2>
        )}

        {/* Dynamic Content Sections */}
        {blog.content && (
          <div className="space-y-6 mb-10">
            {blog.content.map((section: any, idxPer: number) => {
              if (section.type === "paragraph") {
                return (
                  <p key={idxPer} className="text-gray-700 text-justify leading-relaxed">
                     {formatTrademark(section.text)}
                  </p>
                );
              }

              if (section.type === "conclusion-paragraph") {
                return (
                  <p key={idxPer} className="text-gray-700 text-justify leading-relaxed lg:mb-30">
                     {formatTrademark(section.text)}
                  </p>
                );
              }
              
              if (section.type === "heading") {
                return (
                  <h2 key={idxPer} className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                     {formatTrademark(section.text)}
                  </h2>
                );
              }
              if (section.type === "list") {
                return (
                  <div key={idxPer} className="mb-6">
                    {section.title && (
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">
                         {formatTrademark(section.title)}
                      </h3>
                    )}
                    <ul className="list-disc list-inside text-gray-700 space-y-2">
                      {section.items.map((item: string, i: number) => (
                        <li key={i}>{formatTrademark(item)}</li>
                      ))}
                    </ul>
                  </div>
                );
              }
              if (section.type === "image") {
                return (
                  <img
                    key={idxPer}
                    src={section.src}
                    alt={section.alt || "Blog image"}
                    className="width-full mb-6 object-cover"
                    
                  />
                );
              }
              if (section.type === "video") {
                return (
                  <video
                    key={idxPer}
                    src={section.src}
                    controls
                    className="rounded-lg mb-10"
                    style={{
                      width: section.width || "100%",
                      height: section.height || "auto",
                    }}
                  />
                );
              }
              return null;
            })}
          </div>
        )}

        

        {/* Legacy Content (for first blog) */}
        {blog.intro && (
          <p className="text-gray-700 text-justify leading-relaxed mb-4">
             {formatTrademark(blog.intro)}
          </p>
        )}

        {blog.reasons && (
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
            {blog.reasons.map((reason: any, idx: number) => (
              <li key={idx}>
                <span className="font-semibold">{formatTrademark(reason.label)}</span> –{" "}
                {formatTrademark(reason.text)}
              </li>
            ))}
          </ul>
        )}

        {blog.subHeading && (
          <h2 className="text-xl font-semibold text-gray-900 mb-3">
             {formatTrademark(blog.subHeading)}
          </h2>
        )}

        {blog.antibioticsIntro && (
          <p className="text-gray-700 text-justify leading-relaxed mb-4">
             {formatTrademark(blog.antibioticsIntro)}
          </p>
        )}

        {blog.antibioticPoints && (
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
            {blog.antibioticPoints.map((point: string, idx: number) => (
              <li key={idx}>{formatTrademark(point)}</li>
            ))}
          </ul>
        )}

        {blog.conclusion && !Array.isArray(blog.conclusion) && (
          <p className="text-gray-700 text-justify leading-relaxed mb-6">
             {formatTrademark(blog.conclusion)}
          </p>
        )}

        {blog.highlightone && (
          <p className="text-gray-800 text-justify leading-relaxed font-medium mb-5">
            <span className="font-bold">{formatTrademark("CHECK®")}</span>{" "}
            {formatTrademark(blog.highlightone.replace("CHECK®", ""))}
          </p>
        )}

        {blog.highlighttwo && (
          <>
            <h3 className="text-gray-800 mb-4">
               {formatTrademark("CHECK®")} Works through a{" "}
              <span className="font-bold">multi-target approach, </span>
              ensuring quick and complete recovery:
            </h3>
            <ol className="list-decimal list-inside text-gray-700 space-y-2 mb-4 marker:font-bold">
              {blog.highlighttwo.multiTargetApproach.map((point: string, idx: number) => (
                <li key={idx}>{formatTrademark(point)}</li>
              ))}
            </ol>

            <p className="text-gray-700 mb-4 text-justify">
               {formatTrademark(blog.highlighttwo.combinedAction)}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mb-10">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Benefits at a Glance
                </h4>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  {blog.highlighttwo.benefits.map((benefit: string, idx: number) => (
                    <li key={idx}>{formatTrademark(benefit)}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Dosage & Usage
                </h4>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  {blog.highlighttwo.dosage.map((dose: string, idx: number) => (
                    <li key={idx}>{formatTrademark(dose)}</li>
                  ))}
                </ul>
                <h4 className="text-lg font-semibold text-gray-900 mt-4 mb-2">
                  Presentation
                </h4>
                <ul className="list-disc list-inside text-gray-700 text-justify space-y-1">
                  {blog.highlighttwo.presentation.map((item: string, idx: number) => (
                    <li key={idx}>{formatTrademark(item)}</li>
                  ))}
                </ul>
                <h4 className="text-lg font-semibold text-gray-900 mt-4 mb-2">
                  Conclusion
                </h4>
                <ul className="list-disc list-inside text-gray-700 space-y-1 text-justify lg:mb-20">
                  {Array.isArray(blog.highlighttwo.conclusion)
                    ? blog.highlighttwo.conclusion.map((text: string, idx: number) => (
                        <li key={idx}>{formatTrademark(text)}</li>
                      ))
                    : formatTrademark(blog.highlighttwo.conclusion)}
                </ul>
              </div>
            </div>
          </>
        )}

        
      </div>
    </>
  );
};

export default SpecificBlog;
